import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["trust_session_id"];

/**
 * SDK wrapper for Transunion APIs.
 * See https://github.com/NerdWallet/transunion for details.
 */
import dataApiFactory from './data-api-factory';
import serviceHTTPHelper from './service-http-helper';

var TransunionSDK = function TransunionSDK(ServiceSDK, config) {
  var transunionBaseUrl = config.transunionBaseUrl;
  var identityAntifraudPass = config.identityAntifraudPass;

  if (!transunionBaseUrl) {
    throw new Error('TransunionSDK requires a valid transunionBaseUrl');
  }

  function getTransunionRoute(namespace, version) {
    return function (endpoint) {
      return ServiceSDK.nwApiRoute(transunionBaseUrl, namespace, version, endpoint);
    };
  }

  function buildDataApiFactory(namespace, version) {
    return dataApiFactory(ServiceSDK, getTransunionRoute(namespace, version));
  }

  var service = serviceHTTPHelper(ServiceSDK, getTransunionRoute('api', 1)); // ---- Profiles Interface ----

  var profiles = {
    /*
      Get batch profiles API for transunion, optionally specifying API versions for each profile.
      - includeProfiles: {profile_name: api_version}
       If api_version is null, the default behavior is to use the highest api version. New clients should
      explicitly choose an api version to avoid unexpected version changes.
       Example usage:
        sdk = TransunionSDK(serviceSDK, {transunionBaseUrl: ...});
        sdk.profiles.getProfilesV2({'tu_credit_report_profile': 1}, serverRequest).then(...);
    */
    getProfilesV2: function getProfilesV2() {
      var includeProfiles = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
      var serverRequest = arguments.length > 1 ? arguments[1] : undefined;
      var apiFactory = buildDataApiFactory('api', 2).getSingularIdentityEntityAPI('profiles');
      var params = {
        include_profiles: JSON.stringify(includeProfiles)
      };
      return apiFactory.getMy(params, serverRequest);
    },

    /*
      Get batch profiles API for transunion, optionally specifying API versions for each profile.
      - identityID: UUID that uniquely identifies the member account
      - includeProfiles: {profile_name: api_version}
      If api_version is null, the default behavior is to use the highest api version. New clients should
      explicitly choose an api version to avoid unexpected version changes.
      Example usage:
        sdk = UserDataPlatformSDK(serviceSDK, {transunionBaseUrl: ...});
        const profiles = await sdk.profiles.getAnyProfilesV2(identityID, {'tu_credit_report_profile': 1}, serverRequest);
    */
    getAnyProfilesV2: function getAnyProfilesV2(identityID) {
      var includeProfiles = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
      var serverRequest = arguments.length > 2 ? arguments[2] : undefined;
      var apiFactory = buildDataApiFactory('api', 2).getSingularIdentityEntityAPI('profiles');
      var params = {
        include_profiles: JSON.stringify(includeProfiles)
      };
      return apiFactory.get(identityID, params, serverRequest);
    }
  }; // ---- TU Interface ----
  // Creates a TransUnion customer in the Identity codebase. This will often require verification questions, or further
  // information before the score and report details are provided
  // - customer: Object - The customer data provided by the user
  // - customer.trust_session_id: String - The session ID for TU digital verification
  // The customer object is passed in, instead of individual arguments because the
  // customer can include different data based on what the user provides.

  function createTUCustomer(data, serverRequest) {
    var trust_session_id = data.trust_session_id,
        customer = _objectWithoutProperties(data, _excluded);

    var url = 'credit_profile/initialize';

    if (identityAntifraudPass) {
      url += '?antifraud=PASS';
    }

    return service.post(url, {
      customer: customer,
      trust_session_id: trust_session_id
    }, serverRequest);
  } // Sends answers about the TU Customer to the authentication endpoint to verify identity
  // - answers - Array
  // Each item in the array is a dict with answer_id and question_id


  function authenticateTUCustomer(answers, serverRequest) {
    return service.post('credit_profile/authenticate', {
      answers: answers
    }, serverRequest);
  } // Normally enrollment happens as part of `authenticateTUCustomer`
  // In some cases, enrollment doesn't complete and our users end up in an 'AUTHENTICATED' state
  // rather than 'ACTIVE'. This endpoint is a means to complete enrollment without starting over.


  function enrollTUCustomer(serverRequest) {
    return service.post('credit_profile/enroll', {}, serverRequest);
  } // Forces a refresh of a user's TU credit report profile. For contractual reasons this should
  // only be called in response to a user action related to an alert.
  // - success: function(TUAuthenticationStatusProfile)
  // - error: function(err, response, data)


  function refreshTUCreditProfile(serverRequest) {
    return service.post('credit_profile/refresh', {}, serverRequest);
  } // Deactivates a user's TU credit report subscriptions and purges all persistant data
  // - success: function(TUAuthenticationStatusProfile)
  // - error: function(err, response, data)


  function deactivateTUCustomer(serverRequest) {
    return service.post('credit_profile/deactivate', {}, serverRequest);
  } // Retrieves the credit score for the current user - if it exists
  // - success: function ({vantage_score_3: some_score})
  // - error: function (err, resp, data)
  //    - NotFoundError - User needs to authenticate for TU
  //    - PreconditionFailedError - Can not retrieve credit score at this time


  function getCreditScore(serverRequest) {
    return service.get('credit_profile/credit_score', {}, serverRequest);
  } // Retrieves the credit score for the current user - if it exists
  // - success: function ({creditFactor: some_value})
  // - error: function (err, resp, data)
  //    - NotFoundError - User needs to authenticate for TU
  //    - PreconditionFailedError - Can not retrieve credit score at this time


  function getCreditFactor(creditFactor, serverRequest) {
    return service.get("credit_profile/".concat(creditFactor), {}, serverRequest);
  } // Retrieves a user's credit factor history for a provided date window, including the entire duration of the end_date
  // - creditFactor: name of requested credit factor [REQUIRED]
  // - startDate: inclusive start of history window [REQUIRED]
  // - endDate: inclusive end of history window [REQUIRED]
  // - interval: type of interval, 'week' or 'month'


  function getCreditFactorHistory(creditFactor, startDate, endDate, interval, serverRequest) {
    var params = {
      start_date: startDate.toISOString(),
      end_date: endDate.toISOString(),
      interval: interval
    };
    return service.get("credit_profile/".concat(creditFactor, "/history"), params, serverRequest);
  } // Retrieves the simulated credit score for the current user given the score modifiers
  // - args is a dict where keys will be from the following list and value will be values for those modifiers
  //    - on_time_payments_num_months: maintain on time payments for # of months (int)
  //    - close_oldest_credit_card: close the oldest credit card (bool)
  //    - one_account_past_due_num_days: let one account go past due (str: 30,60,90)
  //    - all_accounts_past_due_num_days: let all accounts go past due (str: 30,60,90)
  //    - increase_credit_balance_amount: increase credit card balance by amount (int)
  //    - decrease_credit_balance_amount: decreasecredit card balance by amount (int)
  //    - increase_credit_card_limit: increase credit card limit by amount (int)
  //    - move_one_account_into_collection: let one account go into collection (bool)
  //    - add_tax_lien: add a tax lien to public record(bool)
  //    - add_wage_garnishment: add a wage garnishment to public record(bool)
  //    - add_foreclosure: add foreclosure to public record(bool)
  //    - add_child_support: add child support to public record(bool)
  //    - pay_off_all_credit_cards: pay off all credit cards (bool)
  //    - apply_for_number_of_credit_card: apply for number of credit cards (int)
  //    - obtain_credit_card_limit: obtain credit card with limit (int)
  //    - obtain_mortgage_limit: obtain mortgage with limit (int)
  //    - obtain_auto_loan_limit: obtain auto loan with limit (int)
  //    - obtain_personal_loan_limit: obtain personal loan with limit (int)
  //    - transfer_credit_balance_amount: transfer credit balance amount (int)
  // - success: function ({simulated_vantage_score: some_value})
  // - error: function (err, resp, data)


  function simulateVantageScore(args, serverRequest) {
    return service.post('credit_profile/simulate_vantage_score', args, serverRequest);
  } // Retrieves a TU alert by ID
  // - alertId: ID of the requested TU alert


  function getCreditProfileAlert(alertId, serverRequest) {
    return service.get("credit_profile/alert/".concat(alertId), {}, serverRequest);
  } // Retrieves all TU alerts within a range
  // - startDate: inclusive start of window (treats as date without time)
  // - endDate: inclusive end of window (treats as date without time)


  function getCreditProfileAlerts(startDate, endDate, serverRequest) {
    var params = {
      start_date: startDate.toISOString(),
      end_date: endDate.toISOString()
    };
    return service.get('credit_profile/alerts', params, serverRequest);
  } // Retrieves a list of credit factor changes, in order of descending importance


  function getCreditProfileRecentChanges(serverRequest) {
    return service.get('credit_profile/recent_changes', {}, serverRequest);
  }

  return {
    ServiceSDK: ServiceSDK,
    createTUCustomer: createTUCustomer,
    authenticateTUCustomer: authenticateTUCustomer,
    enrollTUCustomer: enrollTUCustomer,
    deactivateTUCustomer: deactivateTUCustomer,
    getCreditScore: getCreditScore,
    getCreditFactor: getCreditFactor,
    getCreditFactorHistory: getCreditFactorHistory,
    simulateVantageScore: simulateVantageScore,
    getCreditProfileAlert: getCreditProfileAlert,
    getCreditProfileAlerts: getCreditProfileAlerts,
    refreshTUCreditProfile: refreshTUCreditProfile,
    getCreditProfileRecentChanges: getCreditProfileRecentChanges,
    profiles: profiles
  };
};

export default TransunionSDK;